import type { Option } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { DefaultBinderHelpers } from "@tento-lona/sheets-ui/sheet/binder/default-binder-helpers.ts";
import type {
  CalendarMonthSubcolumn,
} from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";
import type { CalendarMonthHost } from "./calendar-month.ts";
import type { GestureCoordinatorLike } from "@tento-lona/sheets-ui/sheet/gestures/gesture-system.ts";
import { Tracing } from "@tento-core/tracing";

type CalendarMonthCapabilityPlugin = {
  contentProviders?: Option<{
    calendarMonth?: CalendarMonthSubcolumn.Provider;
  }>;
};

/**
 * A surface projection — e.g. the calendar's all-day strip, which routes to
 * the header surface. Its calendar-list aggregates the SAME events as the
 * body, so it must NOT contribute a month-grid subcolumn: otherwise every
 * event paints twice in month (the body source + the strip source). The
 * month grid renders body content only; the strip is header-only and (in
 * month) folds into the grid cells anyway.
 */
function isSurfaceProjection(row: SheetRow.Joined.Handle): boolean {
  // The discovery walks the VIEW tree, whose rows carry the lona-js DOM
  // descriptor (`descriptor.id`), NOT the rows-side SDK descriptor
  // (`role`/`kind`, which are undefined here). The all-day strip is a surface
  // projection (`id === "projection"`); it aggregates the SAME events as the
  // body, so skipping it keeps the month grid at one subcolumn per calendar
  // instead of two (every event painted twice).
  return row.descriptor.id === "projection";
}

/**
 * Walk children of the layout-bound row; for each capability plugin
 * that declares `contentProviders.calendarMonth`, ask for a subcolumn
 * and thread the layout's host through. First-match per child.
 *
 * Shared between `GridLayoutPlugin` (the grid layout) and
 * `TimelineColumnLayoutPlugin` (which spins up a calendar-month under
 * `sheetTimescale=month`). Renderers declare their providers once and
 * both layouts pick them up.
 */
export function discoverCalendarMonthSubcolumns(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
  calendar: CalendarMonthHost,
  coordinator: GestureCoordinatorLike,
  caller: string = "unknown",
): CalendarMonthSubcolumn[] {
  const host: CalendarMonthSubcolumn.Host = {
    $calendar: calendar.$calendar,
    gestureManager: calendar.gestureManager,
    coordinator,
    system: calendar,
    delegate: calendar.delegate,
    shouldBlockForCrossSystemDismissal: (isPointerDown) =>
      calendar.shouldBlockForCrossSystemDismissal(isPointerDown),
  };

  const subcolumns: CalendarMonthSubcolumn[] = [];
  return Tracing.span("calendar-month/discover-subcolumns", (s) => {
    s.record("caller", caller);
    s.record("rowId", row.id.toString());
    // Alias pass-through: when GridLayoutPlugin is bound to a
    // calendar alias row (rather than to the synthesized grid layout
    // row directly), `row.children()` returns just `[grid]` — a single
    // layout row that itself wraps the calendars/secondary-timezones
    // alias children. Walk through any single-child chain until we
    // reach a row whose children include the calendar-list aliases.
    let walkRow = row;
    let walkDepth = 0;
    let children = walkRow.children() ?? [];
    for (let only = children[0]; children.length === 1 && only !== undefined; only = children[0]) {
      walkRow = only;
      walkDepth += 1;
      children = walkRow.children() ?? [];
      if (walkDepth > 4) break; // safety: don't walk forever
    }
    s.record("walkDepth", walkDepth);
    s.record("effectiveRowId", walkRow.id.toString());
    s.record("childCount", children.length);
    const childSummary: string[] = [];
    let pluginsConsidered = 0;
    let providersFound = 0;

    // Walk a row + its descendants, looking for a plugin that declares
    // contentProviders.calendarMonth. The calendar-list alias for
    // example doesn't expose the provider directly — it sits on the
    // grid-event renderer that resolves under the alias's `events ›
    // renderer-grid` subtree. Returns the first provider hit + the
    // descendant row to bind it through.
    const findProviderInSubtree = (
      start: SheetRow.Joined.Handle,
      depth: number,
    ): Option<{
      provider: NonNullable<NonNullable<CalendarMonthCapabilityPlugin["contentProviders"]>["calendarMonth"]>;
      target: SheetRow.Joined.Handle;
    }> => {
      if (depth > 6) return null;
      const plugins = DefaultBinderHelpers.resolveRowCapabilityPlugins(start, pluginManager);
      pluginsConsidered += 1;
      for (const plugin of plugins) {
        const provider = (plugin as CalendarMonthCapabilityPlugin).contentProviders
          ?.calendarMonth;
        if (provider) return { provider, target: start };
      }
      for (const sub of start.children() ?? []) {
        if (isSurfaceProjection(sub)) continue;
        const hit = findProviderInSubtree(sub, depth + 1);
        if (hit) return hit;
      }
      return null;
    };

    for (const child of children) {
      // Skip the all-day strip (a header-surface projection): it aggregates
      // the same events as the body, so a second subcolumn from it paints
      // every event twice in month. See `isSurfaceProjection`.
      if (isSurfaceProjection(child)) continue;
      const plugins = DefaultBinderHelpers.resolveRowCapabilityPlugins(child, pluginManager);
      const pluginList = [...plugins];
      childSummary.push(
        `${child.id.toString()}[plugins=${pluginList.length}]`,
      );
      const hit = findProviderInSubtree(child, 0);
      if (!hit) continue;
      providersFound += 1;
      // Use `child` (the layout's direct child — e.g. the calendar-list
      // alias) as the subcolumn's row context, not `hit.target` (a deep
      // descendant like the renderer-grid row). Downstream consumers
      // expect the row to be the event-bearing aggregate so they can
      // resolve the events facade off of it.
      const subcolumn = hit.provider.subcolumnFromRow(
        // No sheet view-model at this seam (the provider contract says so).
        null,
        child,
        row,
        host,
      );
      if (!subcolumn) continue;
      subcolumns.push(subcolumn);
    }
    s.record("children", childSummary.join(" | "));
    s.record("pluginsConsidered", pluginsConsidered);
    s.record("providersFound", providersFound);
    s.record("subcolumnsReturned", subcolumns.length);
    if (children.length > 0 && subcolumns.length === 0) {
      // Layout has children but no calendarMonth providers were found.
      // This is the smoking gun for "month view renders but events are
      // missing" — discovery isn't picking up the renderer's provider.
      s.setLevel("error");
      s.record("warning", "no-calendar-month-providers-found");
    }
    return subcolumns;
  });
}
